import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getWebOrigin } from '../../utils/helpers';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { QRCodeCanvas } from 'qrcode.react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useProfileImage } from '../hooks/useProfileImage';
import Breadcrumb from '../../components/common/Breadcrumb';
import OverviewTab from '../tabs/OverviewTab';
import RecentActivityTab from '../tabs/RecentActivityTab';
import TeamsTab from '../tabs/TeamsTab';
import AchievementsTab from '../tabs/AchievementsTab';
import ProfileEditPage from './ProfileEditPage';
import ShareModal from '../../components/common/ShareModal';
import MyStatisticsTab from '../tabs/MyStatisticsTab';
import Tabs from '../../components/common/Tabs';
import {
  User, Trophy, Users, Share2, BarChart3, MapPin,
  QrCode, X, Info, Eye, RefreshCw, Users2, Banknote } from 'lucide-react';
import ManagedPlayersTab from '../tabs/ManagedPlayersTab';

import PayoutsSettings from '../../payments/components/PaymentPayoutsSettings';

const VALID_PROFILE_TABS = ['personal', 'managed-players', 'teammates', 'achievements', 'statistics', 'payouts'];

export default function PlayerProfilePage({ defaultTab = 'personal' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshProfile } = useAuth();
  const { theme } = useTheme();
  // `?tab=` deep-links into an outer tab (e.g. from the Me quick-nav panel). Values meant
  // for ProfileEditPage's own sections (password/notifications/security/danger) aren't
  // valid outer tabs, so they fall through to 'personal' — exactly where the embedded
  // ProfileEditPage lives to pick the same query param up for its own section.
  const tabParam = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTab] = useState(
    VALID_PROFILE_TABS.includes(tabParam) ? tabParam : defaultTab
  );
  const [showQRCode, setShowQRCode] = useState(false);
  const { imageUrl: profileImageUrl } = useProfileImage(user?.id, user?.profileImage);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const qrCodeRef = useRef(null);

  // Sync activeTab with route changes — including `?tab=` (e.g. arriving from the Me
  // quick-nav panel while already on /profile, where the mount-time useState initializer
  // above never re-runs).
  useEffect(() => {
    setActiveTab(VALID_PROFILE_TABS.includes(tabParam) ? tabParam : defaultTab);
  }, [defaultTab, tabParam]);

  // Build breadcrumb items based on current path
  const breadcrumbItems = useMemo(() => {
    const path = location.pathname;

    if (path === '/profile/teams') {
      return [
        { label: 'Profile', path: '/profile' },
        { label: 'Teams' }
      ];
    }

    // No breadcrumbs for main profile page
    return null;
  }, [location.pathname]);

  // Generate profile URL for QR code
  const getProfileUrl = () => {
    return `${getWebOrigin()}/player/${user?.id || ''}`;
  };

  // Download QR code as image
  const handleDownloadQR = () => {
    const canvas = document.getElementById('qr-code-canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${user?.name || 'profile'}-qr-code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('QR code downloaded successfully!');
    }
  };

  // Refresh profile data from API
  const handleRefreshProfile = async () => {
    setIsRefreshing(true);
    const result = await refreshProfile();
    if (result.success) {
      toast.success('Profile refreshed successfully!');
    } else {
      toast.error(result.error || 'Failed to refresh profile');
    }
    setIsRefreshing(false);
  };

  if (!user) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="text-center">
          <h2 className={`text-2xl font-bold ${theme.text} mb-4`}>Please sign in to view your profile</h2>
          <button
            onClick={() => navigate('/signin')}
            className={`px-6 py-3 ${theme.primary} rounded-lg ${theme.primaryHover} transition-colors`}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.background} py-6 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb - only show when on sub-pages */}
        {breadcrumbItems && (
          <div className="mb-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        )}

        {/* Profile Header */}
        <div className={`${theme.card} rounded-xl ${theme.shadow} overflow-hidden mb-8`}>
          <div className={`h-32 ${theme.gradient}`}></div>
          <div className="px-6 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end -mt-12 sm:-mt-16">
              <div className="relative">
                <div className={`w-24 h-24 sm:w-32 sm:h-32 ${theme.background} rounded-full border-4 ${theme.border} ${theme.shadow} flex items-center justify-center overflow-hidden`}>
                  {profileImageUrl ? (
                    <img
                      src={profileImageUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-3xl sm:text-4xl font-bold text-white">
                        {user?.firstName?.charAt(0)?.toUpperCase() || user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 sm:mt-0 sm:ml-6 flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h1 className={`text-2xl sm:text-3xl font-bold ${theme.text}`}>{user.name}</h1>
                    <p className={`${theme.textSecondary} flex items-center mt-1`}>
                      <MapPin className="w-4 h-4 mr-1" />
                      {user.location || 'Location not set'}
                    </p>
                  </div>
                  <div className="mt-4 sm:mt-0 flex gap-x-3">
                    <button
                      onClick={handleRefreshProfile}
                      disabled={isRefreshing}
                      className={`px-4 py-2 border ${theme.border} ${theme.textSecondary} rounded-lg ${theme.cardHover} transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed`}
                      title="Refresh profile data"
                    >
                      <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={() => navigate(`/player/${user?.userHandle || user?.id}`, { state: { fromProfile: true } })}
                      className={`px-4 py-2 border ${theme.border} ${theme.textSecondary} rounded-lg ${theme.cardHover} transition-colors flex items-center gap-2`}
                      title="View your public profile"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">Public Profile</span>
                    </button>
                    <button
                      onClick={() => setShowShareModal(true)}
                      className={`px-4 py-2 border ${theme.border} ${theme.textSecondary} rounded-lg ${theme.cardHover} transition-colors flex items-center`}
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </button>
                    <button
                      onClick={() => setShowQRCode(true)}
                      className={`px-4 py-2 border ${theme.border} ${theme.textSecondary} rounded-lg ${theme.cardHover} transition-colors flex items-center`}
                      title="Show QR Code"
                    >
                      <QrCode className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full">
          {/* Main Content */}
          <div className="space-y-6">
            {/* Tab Navigation */}
            <Tabs
              tabs={[
                { id: 'personal', label: 'Personal Info', shortLabel: 'Personal', icon: User },
                { id: 'managed-players', label: 'Managed Players', shortLabel: 'Managed', icon: Users2 },
                { id: 'teammates', label: 'My Teams', shortLabel: 'Teams', icon: Users },
                { id: 'achievements', label: 'My Achievements', shortLabel: 'Achievements', icon: Trophy },
                { id: 'payouts', label: 'Payouts', shortLabel: 'Payouts', icon: Banknote },
                { id: 'statistics', label: 'My Statistics', shortLabel: 'Stats', icon: BarChart3 }
              ]}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              mobileVisibleTabs={['personal', 'managed-players', 'teammates']}
              className="mb-6 -mx-4 px-4 sm:mx-0 sm:px-0"
            />

            {/* Tab Content */}
            <div className={`${theme.card} rounded-xl ${theme.shadow}`}>
              <div className="p-6">
                {activeTab === 'personal' && (
                  <ProfileEditPage embedded={true} onProfileImageUpdate={refreshProfile} />
                )}

                {activeTab === 'managed-players' && (
                  <ManagedPlayersTab />
                )}

                {activeTab === 'teammates' && (
                  <TeamsTab userId={user?.id} />
                )}

                {activeTab === 'achievements' && (
                  <AchievementsTab />
                )}

                {activeTab === 'payouts' && (
                  <PayoutsSettings
                    ownerType="user"
                    ownerId={user?.id}
                    returnPath="/profile?tab=payouts&connect=return"
                  />
                )}
                {activeTab === 'statistics' && (
                  <MyStatisticsTab userId={user?.id} />
                )}

              </div>
            </div>
          </div>
        </div>

        {/* QR Code Modal */}
        {showQRCode && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`${theme.card} rounded-xl max-w-md w-full p-8`}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className={`text-lg font-semibold ${theme.text}`}>Profile QR Code</h3>
                  <p className={`text-sm ${theme.textSecondary}`}>Scan to view {user.name}'s profile</p>
                </div>
                <button
                  onClick={() => setShowQRCode(false)}
                  className={`p-2 ${theme.cardHover} rounded-full transition-colors`}
                >
                  <X className={`w-5 h-5 ${theme.textMuted}`} />
                </button>
              </div>

              <div className="text-center">
                {/* QR Code Image */}
                <div className={`inline-block p-6 ${theme.background} border-2 ${theme.border} rounded-lg mb-4 ${theme.shadow}`}>
                  <QRCodeCanvas
                    id="qr-code-canvas"
                    value={getProfileUrl()}
                    size={256}
                    level="H"
                    includeMargin={true}
                  />
                </div>

                <p className={`text-sm ${theme.textSecondary} mb-4`}>
                  Scan this QR code to access this profile or use it for attendance tracking
                </p>

                <div className={`${theme.backgroundSecondary} rounded-lg p-3 mb-4`}>
                  <p className={`text-xs ${theme.textMuted} break-all`}>
                    {getProfileUrl()}
                  </p>
                </div>

                <div className="flex gap-x-3">
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(getProfileUrl());
                      toast.success('Profile URL copied to clipboard!');
                    }}
                    className={`flex-1 px-4 py-2 ${theme.primary} rounded-lg ${theme.primaryHover} transition-colors text-sm`}
                  >
                    Copy URL
                  </button>
                  <button
                    onClick={handleDownloadQR}
                    className={`flex-1 px-4 py-2 border ${theme.border} ${theme.textSecondary} rounded-lg ${theme.cardHover} transition-colors text-sm`}
                  >
                    Download
                  </button>
                </div>
              </div>
            </div>
        </div>
        )}

        {/* Share Modal */}
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          entity={{
            id: user?.id,
            name: user?.name || `${user?.firstName} ${user?.lastName}`,
            description: `Check out ${user?.name || `${user?.firstName} ${user?.lastName}`}'s profile on Kynaix Sports`
          }}
          entityType="profile"
          urlPattern="/player/{id}"
          currentUser={user}
        />
      </div>
    </div>
  );
}